import axios from "axios";
import { BASE_API } from "../../constants/api";

const REFRESH_TIMEOUT_MS = 10000;
const ACCESS_REFRESH_MARGIN_SECONDS = 30;
const CREDENTIALS_CHANGED_EVENT = "auth-credentials-changed";
const STORAGE_KEYS = Object.freeze({
    ACCESS: "token",
    REFRESH: "refreshToken",
    USER: "user",
    PROFILE_POPUP: "profilePopupDismissed",
});
const REJECTED_REFRESH_STATUSES = new Set([400, 401, 403]);

let authenticationRevision = 0;
let pendingRefresh = null;

// Expiry is a client scheduling hint. The backend verifies JWT signatures.
export function tokenExpiry(token) {
    try {
        const encodedPayload = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        const payload = JSON.parse(atob(encodedPayload));
        return Number.isFinite(payload.exp) ? payload.exp : 0;
    } catch (_) {
        return 0;
    }
}

function announceCredentialChange() {
    window.dispatchEvent(new Event(CREDENTIALS_CHANGED_EVENT));
}

export function invalidateRefresh() {
    authenticationRevision++;
    // A new login must not wait for the previous login's HTTP request.
    pendingRefresh = null;
}

export function clearCredentials() {
    invalidateRefresh();
    Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
    announceCredentialChange();
}

export function redirectToLogin() {
    clearCredentials();
    window.location.href = "/login";
}

function readAuthenticationIdentity() {
    return {
        revision: authenticationRevision,
        refreshToken: localStorage.getItem(STORAGE_KEYS.REFRESH),
    };
}

function isCurrentAuthentication(identity) {
    return identity.revision === authenticationRevision &&
        identity.refreshToken === localStorage.getItem(STORAGE_KEYS.REFRESH);
}

function validateRefreshResponse(data) {
    if (typeof data?.access !== "string" || tokenExpiry(data.access) <= Date.now() / 1000) {
        throw new Error("Invalid refresh response");
    }
    if (data.refresh !== undefined &&
        (typeof data.refresh !== "string" || tokenExpiry(data.refresh) <= Date.now() / 1000)) {
        throw new Error("Invalid refresh response");
    }
}

function prepareUserUpdate(accessToken) {
    const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
    if (!storedUser) return null;
    // Parse and validate before writing any credentials.
    const user = JSON.parse(storedUser);
    if (!user || typeof user !== "object" || Array.isArray(user)) {
        throw new Error("Invalid stored user");
    }
    return JSON.stringify({ ...user, expiresIn: tokenExpiry(accessToken) });
}

function saveRefreshedCredentials(data, updatedUser) {
    localStorage.setItem(STORAGE_KEYS.ACCESS, data.access);
    if (data.refresh !== undefined) localStorage.setItem(STORAGE_KEYS.REFRESH, data.refresh);
    if (updatedUser !== null) localStorage.setItem(STORAGE_KEYS.USER, updatedUser);
    announceCredentialChange();
}

async function requestAndSaveAccessToken(request) {
    try {
        const response = await axios.post(
            `${BASE_API}token/refresh/`,
            { refresh: request.identity.refreshToken },
            { timeout: REFRESH_TIMEOUT_MS },
        );
        if (!isCurrentAuthentication(request.identity)) {
            throw new Error("Authentication changed");
        }
        validateRefreshResponse(response.data);
        const updatedUser = prepareUserUpdate(response.data.access);
        saveRefreshedCredentials(response.data, updatedUser);
        return response.data.access;
    } catch (error) {
        const refreshRejected = REJECTED_REFRESH_STATUSES.has(error?.response?.status);
        if (refreshRejected && !request.preserveCredentialsOnFailure &&
            isCurrentAuthentication(request.identity)) {
            redirectToLogin();
        }
        throw error;
    } finally {
        // An old request must not clear a newer login's pending refresh.
        if (pendingRefresh === request) pendingRefresh = null;
    }
}

export async function refreshAccess({ preserveCredentialsOnFailure = false } = {}) {
    const identity = readAuthenticationIdentity();
    if (pendingRefresh && isCurrentAuthentication(pendingRefresh.identity)) {
        // Explicit logout takes precedence over background failure cleanup.
        pendingRefresh.preserveCredentialsOnFailure ||= preserveCredentialsOnFailure;
        return pendingRefresh.promise;
    }
    if (!identity.refreshToken) {
        if (!preserveCredentialsOnFailure) redirectToLogin();
        throw new Error("Login required");
    }

    const request = { identity, preserveCredentialsOnFailure, promise: null };
    pendingRefresh = request;
    request.promise = requestAndSaveAccessToken(request);
    return request.promise;
}

export async function validAccess(options = {}) {
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS);
    const refreshThreshold = Date.now() / 1000 + ACCESS_REFRESH_MARGIN_SECONDS;
    if (accessToken && tokenExpiry(accessToken) > refreshThreshold) return accessToken;
    return refreshAccess(options);
}
